import React from 'react';
import {
  Info,
  Money,
  Search,
  Send as SendIcon,
  Person as PersonIcon,
  LocalShipping as LocalShippingIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from '@mui/icons-material';
import { useModal } from 'hooks/useModal';
import { useHistory } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import { ModalsKeys, SelectOption } from 'global';
import { InventoryCard } from 'modules/inventory';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNotifications } from 'modules/notification';
import { RadioGroupField, SwitchField } from 'components/form';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { PortalConfigurationType } from '@trnsact/trnsact-shared-types';
import { Button, Stack, Theme, Typography, Tooltip } from '@mui/material';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { FormSection } from './FormSection';
import { FinanceSection } from './FinanceSection';
import { TemplateSection } from './TemplateSection';
import { EquipmentSection } from './EquipmentSection';
import { sendApplicationFormSchema } from '../../schema';
import { SendApplicationFormValues } from '../../types';
import { ContactDetailsSection } from './ContactDetailsSection';
import { PortalConfigurationRolesSettingsKeys } from '../../../../types';
import { usePortalConfigurationChecks } from 'modules/portalConfigurations';
import { sendMethodOptions, TestIds, sendApplicationFormInitialValues } from '../../constants';

interface Props {
  isSendOcaLinkLoading: boolean;
  lenderProfilesWithOcaOptions: SelectOption[];
  onSendOcaLink: (values: SendApplicationFormValues) => Promise<string | null>;
}

export const SendApplicationForm = ({ onSendOcaLink, isSendOcaLinkLoading, lenderProfilesWithOcaOptions }: Props) => {
  const classes = useStyles();

  const history = useHistory();

  const { checkPortalConfigEntryForCurrentUserRole } = usePortalConfigurationChecks();

  const isInventoryFeatureActive = [
    PortalConfigurationRolesSettingsKeys.ReadOnly,
    PortalConfigurationRolesSettingsKeys.AssignedOnly,
  ].some(key => checkPortalConfigEntryForCurrentUserRole(PortalConfigurationType.Inventory, key));

  const { showNotification } = useNotifications();
  const { handleOpen: handleOpenSearchInventoryDialog } = useModal(ModalsKeys.SearchInventoryDialog);

  const form = useForm<SendApplicationFormValues>({
    resolver: zodResolver(sendApplicationFormSchema),
    defaultValues: sendApplicationFormInitialValues,
  });

  const selectedInventory = useWatch({ control: form.control, name: 'listOfInventory' });

  const handleSubmit = async (formValues: SendApplicationFormValues) => {
    const link = await onSendOcaLink(formValues);

    if (link) {
      showNotification('Link sent successfully!');
      form.reset();
    } else {
      showNotification('Error', { type: 'error' });
    }
  };

  return (
    <FormProvider {...form}>
      <RadioGroupField
        row
        name="sendMethod"
        control={form.control}
        options={sendMethodOptions}
        label="Send your credit app link"
        data-testid={TestIds.SendViaRadioGroup}
      />

      <FormSection title="Contact Details" icon={<PersonIcon className={classes.formSectionIcon} />}>
        <ContactDetailsSection />
      </FormSection>

      <FormSection
        isExpanded
        isExpandable
        title="Equipment"
        icon={<LocalShippingIcon className={classes.formSectionIcon} />}
        actionSlot={
          <Stack width="100%" direction="row" gap="0.5rem" justifyContent="flex-end" alignItems="center">
            <Tooltip
              arrow
              disableHoverListener={!!selectedInventory.length}
              title={
                <Typography variant="body1">Please select an item from inventory to send equipment flyer</Typography>
              }
            >
              <span>
                <SwitchField
                  size="small"
                  name="isPromoteFlyer"
                  control={form.control}
                  disabled={!selectedInventory.length}
                  label={
                    <Stack direction="row" gap="0.5rem" alignItems="center">
                      <Typography>Promote Flyer</Typography>
                      <Tooltip title="Prospect will see equipment flyer before credit application page">
                        <Info fontSize="small" color="primary" />
                      </Tooltip>
                    </Stack>
                  }
                />
              </span>
            </Tooltip>

            <RenderWithCondition condition={isInventoryFeatureActive}>
              <Button
                startIcon={<Search />}
                size="small"
                variant="text"
                onClick={event => {
                  event.stopPropagation();
                  handleOpenSearchInventoryDialog({
                    onSubmit: async listOfEquipmentId => form.setValue('listOfInventory', listOfEquipmentId),
                  });
                }}
              >
                Search Inventory
              </Button>
            </RenderWithCondition>
          </Stack>
        }
      >
        <EquipmentSection />

        <RenderWithCondition condition={!!selectedInventory.length}>
          <Stack gap="0.5rem" pt="0.75rem">
            <Typography fontWeight={500}>Selected Inventory:</Typography>

            {selectedInventory.map(inventoryId => (
              <InventoryCard
                id={inventoryId}
                key={inventoryId}
                onRemove={() => {
                  const filteredList = selectedInventory.filter(id => id !== inventoryId);
                  if (!filteredList.length) form.setValue('isPromoteFlyer', false);
                  form.setValue('listOfInventory', filteredList);
                }}
                onViewUnit={() => {
                  history.push(`/equipment/${inventoryId}`);
                }}
              />
            ))}
          </Stack>
        </RenderWithCondition>
      </FormSection>

      {!!lenderProfilesWithOcaOptions.length && (
        <FormSection
          title="Credit Application Template"
          icon={<InsertDriveFileIcon className={classes.formSectionIcon} />}
        >
          <TemplateSection lenderProfilesWithOcaOptions={lenderProfilesWithOcaOptions} />
        </FormSection>
      )}

      <RenderWithCondition condition={isInventoryFeatureActive}>
        <FormSection title="Finance" icon={<Money className={classes.formSectionIcon} />}>
          <FinanceSection />
        </FormSection>
      </RenderWithCondition>

      <Button
        variant="contained"
        endIcon={<SendIcon />}
        loading={isSendOcaLinkLoading}
        sx={{ alignSelf: 'flex-start' }}
        onClick={form.handleSubmit(handleSubmit)}
      >
        Send
      </Button>
    </FormProvider>
  );
};

const useStyles = makeStyles<Theme>(({ palette }) => ({
  formSectionIcon: {
    fontSize: 20,
    color: palette.grey[500],
  },
}));
